import { keysToSnakeCase } from '@hotjar/webapp/lib/convert-string';
import type { RegistrationCampaign } from '@hotjar/webapp/domains/campaigns/model';
import { DEFAULT_REGISTRATION_PLAN } from '@hotjar/webapp/domains/campaigns/api';
import * as Segment from '@hotjar/webapp/domains/common/tracking/segment';
import * as googleAnalytics from './tools/google-analytics';
import * as hotjarTracking from './tools/hotjar';

export interface SignupTrackingData {
  signupMethod?: string;
  signupStep: string;
  signupStepNumber: number;
  campaign?: RegistrationCampaign;
  deviceType?: string;
  signupSitePlan?: string;
  stateEventWasTriggeredOn?: string;
  locale?: string;
}

const signupStepProgress = (signupData: SignupTrackingData, action?: 'Submitted' | 'Completed') => {
  const { signupStep, signupStepNumber, campaign, locale } = signupData;
  const signupMethod = signupData.signupMethod ?? '';
  const deviceType = signupData.deviceType ?? '';
  const stateEventWasTriggeredOn = signupData.stateEventWasTriggeredOn ?? '';
  const eventName = action
    ? `Signup Step ${signupStepNumber} ${action}`
    : `Signup Step ${signupStepNumber}`;
  const segmentEventName = action ? `Signup Step ${action}` : 'Signup Step';

  hotjarTracking.tagRecording(eventName);
  googleAnalytics.trackAccountEvent(eventName, signupMethod);
  Segment.track(
    segmentEventName,
    keysToSnakeCase({
      ...(campaign
        ? { trialCampaignHash: campaign.hash, trialCampaignValid: Boolean(campaign.valid) }
        : {}),
      ...(campaign?.details
        ? {
            trialCampaignId: campaign.details.id,
            trialCampaignName: campaign.details.name,
            trialCampaignPlan: campaign.details.plan,
            trialCampaignPeriod: campaign.details.numFreeDays,
            trialCampaignDailyPageviews: campaign.details.numFreeDays,
          }
        : {}),
      signupSitePlan: campaign?.plan ?? DEFAULT_REGISTRATION_PLAN,
      signupMethod,
      deviceType,
      signupStep,
      signupStepNumber,
      stateEventWasTriggeredOn,
      ...(typeof locale === 'string' ? { locale } : {}),
    }),
  );
};

/**
 * Track that the user has advanced to a specific step in the signup process.
 */
export const signupStepLoaded = (signupData: SignupTrackingData): void => {
  signupStepProgress(signupData);
};

/**
 * Track when a signup steo is submitted.
 */
export const signupStepSubmitted = (signupData: SignupTrackingData): void => {
  signupStepProgress(signupData, 'Submitted');
};

/**
 * Track when a signup steo is completed.
 */
export const signupStepCompleted = (signupData: SignupTrackingData): void => {
  signupStepProgress(signupData, 'Completed');
};
